import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBasket, Menu, X, Phone, Mail, Clock } from "lucide-react";
import { useEnquiry } from "@/components/EnquiryCart";
import { STORE } from "@/lib/catalog";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/products", label: "Products", testid: "nav-products-link" },
  { to: "/about", label: "About Us", testid: "nav-about-link" },
  { to: "/contact", label: "Contact Us", testid: "nav-contact-link" },
];

export default function Navbar() {
  const { count, setOpen } = useEnquiry();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const linkClass = ({ isActive }) =>
    `relative text-sm font-semibold tracking-wide transition-colors duration-300 ${
      isActive ? "text-forest" : "text-ink-soft hover:text-forest"
    }`;

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-forest-dark text-bone/90 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
          <a href={`tel:${STORE.phoneIntl.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-saffron transition-colors" data-testid="topbar-phone-link">
            <Phone className="w-3.5 h-3.5" /> {STORE.phoneIntl}
          </a>
          <p className="hidden md:flex items-center gap-2 text-bone/70">
            <Clock className="w-3.5 h-3.5" /> {STORE.hours}
          </p>
          <a href={`mailto:${STORE.email}`} className="flex items-center gap-2 hover:text-saffron transition-colors" data-testid="topbar-email-link">
            <Mail className="w-3.5 h-3.5" /> {STORE.email}
          </a>
        </div>
      </div>

      <nav className="backdrop-blur-md bg-bone/85 border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <span className="w-9 h-9 rounded-full bg-forest text-bone flex items-center justify-center font-serif italic text-lg">S</span>
            <span className="font-serif font-bold text-lg sm:text-xl leading-none">
              Shivam <span className="italic text-marigold">Kirana</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.testid} end={l.to === "/"}>
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 rounded-full bg-forest text-bone px-4 py-2 text-sm font-semibold hover:bg-forest-deep transition-colors duration-300"
              data-testid="nav-enquiry-cart-button"
            >
              <ShoppingBasket className="w-4 h-4" />
              <span className="hidden sm:inline">Enquiry List</span>
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-marigold text-white text-[10px] flex items-center justify-center font-bold" data-testid="cart-count-badge">
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden p-2 text-ink"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden border-t border-line bg-bone"
            >
              <div className="px-6 py-4 flex flex-col gap-4">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `text-base font-semibold ${isActive ? "text-forest" : "text-ink-soft"}`
                    }
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
